/** Short beginner glossary for HelpTip / BeginnerHelp. */

export type BeginnerTermId =
  | 'arv'
  | 'rehabContingency'
  | 'cashOnCashReturn'
  | 'returnOnCost'
  | 'breakEvenSalePrice'
  | 'holdingPeriod'
  | 'loanPoints'
  | 'purchaseClosingCosts'
  | 'sellingCosts'
  | 'agentCommission'
  | 'mao'
  | 'targetCashOnCash'
  | 'rehabCondition'
  | 'pricePerSqft'
  | 'compBasedIndication'
  | 'noi'
  | 'capRate'
  | 'dscr'
  | 'annualCashOnCash'
  | 'vacancy'
  | 'operatingExpenses'
  | 'reserves'
  | 'brrrr'
  | 'refinanceLtv'
  | 'cashLeftInDeal';

export type BeginnerTerm = {
  id: BeginnerTermId;
  label: string;
  short: string;
  detail: string;
};

export const BEGINNER_TERMS: Record<BeginnerTermId, BeginnerTerm> = {
  arv: {
    id: 'arv',
    label: 'ARV',
    short: 'After Repair Value — what the property should sell for after renovations.',
    detail:
      'ARV is your best estimate of the resale price once rehab is finished, based on comparable sold homes nearby. It is not the asking price and not a guaranteed sale price.',
  },
  rehabContingency: {
    id: 'rehabContingency',
    label: 'Rehab contingency',
    short: 'Extra % of rehab budget reserved for surprises.',
    detail:
      'Contingency is a cushion on top of your rehab estimate for unexpected repairs. It is an assumption you can change in the Rehab section.',
  },
  cashOnCashReturn: {
    id: 'cashOnCashReturn',
    label: 'Cash-on-cash return',
    short: 'Projected profit divided by estimated cash you put in for this deal.',
    detail:
      'Cash-on-cash return here is a projected return for this flip/project based on your inputs. It is not annualized — it does not convert the hold period into a yearly rate.',
  },
  returnOnCost: {
    id: 'returnOnCost',
    label: 'Return on cost',
    short: 'Projected profit as a percent of total project cost.',
    detail:
      'Return on cost compares profit to the full project cost (purchase, rehab, hold, financing, and sale costs), not only the cash you personally invested.',
  },
  breakEvenSalePrice: {
    id: 'breakEvenSalePrice',
    label: 'Break-even sale price',
    short: 'Sale price where projected profit is about zero after modeled costs.',
    detail:
      'If you sell below break-even, the deal loses money on paper after the costs and fees DealSkout models. Above it, you have a cushion before profit turns negative.',
  },
  holdingPeriod: {
    id: 'holdingPeriod',
    label: 'Holding period',
    short: 'How many months you expect to own the property before selling.',
    detail:
      'Holding period drives interest carry and monthly costs like tax and insurance for the months you own the home during the flip.',
  },
  loanPoints: {
    id: 'loanPoints',
    label: 'Loan points',
    short: 'Upfront lender fees as a percent of a loan amount your contract defines.',
    detail:
      'One point is typically 1% of a chosen base (purchase loan, total committed loan, or a custom amount). Your lender agreement decides which base applies.',
  },
  purchaseClosingCosts: {
    id: 'purchaseClosingCosts',
    label: 'Purchase closing costs',
    short: 'Buyer-side closing fees as a percent of the purchase price.',
    detail:
      'Includes typical buy-side fees at closing (title, escrow, etc.). This is an assumption — change it if your market or lender differs.',
  },
  sellingCosts: {
    id: 'sellingCosts',
    label: 'Selling costs',
    short: 'Seller-side sale costs (not agent commission) as a percent of ARV.',
    detail:
      'Covers non-commission sale costs such as seller credits or closing fees at exit. Agent commission is tracked separately.',
  },
  agentCommission: {
    id: 'agentCommission',
    label: 'Agent commission',
    short: 'Real estate commission as a percent of the expected sale (ARV).',
    detail:
      'Typically paid from sale proceeds. Adjust if you negotiate a different rate or sell without a traditional listing.',
  },
  mao: {
    id: 'mao',
    label: 'MAO',
    short: 'Maximum allowable offer — a rule-of-thumb purchase ceiling.',
    detail:
      'DealSkout’s MAO rule of thumb is ARV × MAO% − rehab. It is a planning guide, not a lender rule or guaranteed max bid.',
  },
  targetCashOnCash: {
    id: 'targetCashOnCash',
    label: 'Target cash-on-cash return',
    short: 'The cash-on-cash return you want this deal to hit.',
    detail:
      'Used when estimating target purchase prices. It is a projected deal/project return, not an annualized rate.',
  },
  rehabCondition: {
    id: 'rehabCondition',
    label: 'Property condition',
    short: 'How heavy the rehab looks — for guidance only.',
    detail:
      'Condition helps you think about scope. It does not automatically set a dollar rehab budget — enter your own estimate.',
  },
  pricePerSqft: {
    id: 'pricePerSqft',
    label: '$ / sqft',
    short: 'Sale price divided by living square footage.',
    detail:
      'Useful for comparing homes of different sizes. DealSkout only uses comps with a valid sale price and sqft greater than zero when computing $/sqft.',
  },
  compBasedIndication: {
    id: 'compBasedIndication',
    label: 'Comp-based value indication',
    short: 'Subject sqft × median $/sqft from your included comps.',
    detail:
      'This is a quick check from the comps you selected — not an appraisal and not a guaranteed ARV. Your entered ARV still drives Analysis deal math.',
  },
  noi: {
    id: 'noi',
    label: 'NOI',
    short: 'Effective income minus operating expenses, before debt.',
    detail: 'Net operating income is effective gross income minus operating expenses. Debt service is not an operating expense.',
  },
  capRate: {
    id: 'capRate',
    label: 'Cap rate',
    short: 'NOI divided by purchase price.',
    detail: 'Going-in cap rate uses the purchase price. It is undefined when the purchase price is zero.',
  },
  dscr: {
    id: 'dscr',
    label: 'DSCR',
    short: 'NOI divided by annual debt service.',
    detail: 'A cash purchase has no debt service, so DSCR is not applicable. It is not treated as infinity.',
  },
  annualCashOnCash: {
    id: 'annualCashOnCash',
    label: 'Annual cash-on-cash',
    short: 'Annual pre-tax cash flow divided by cash invested.',
    detail: 'This rental cash-on-cash is an annual pre-tax return. It is not the flip project return on cash.',
  },
  vacancy: {
    id: 'vacancy',
    label: 'Vacancy',
    short: 'Share of scheduled rent treated as uncollected.',
    detail: 'Vacancy applies only to gross scheduled rent. Other income is not vacated.',
  },
  operatingExpenses: {
    id: 'operatingExpenses',
    label: 'Operating expenses',
    short: 'Recurring costs of operating the property, excluding the mortgage.',
    detail: 'Taxes, insurance, management, maintenance, reserves, and similar lines are operating expenses. The loan payment is not.',
  },
  reserves: {
    id: 'reserves',
    label: 'Reserves',
    short: 'Money set aside for repairs.',
    detail: 'A blank reserve stays unset and contributes $0 until you enter it or mark it none.',
  },
  brrrr: {
    id: 'brrrr',
    label: 'BRRRR',
    short: 'Buy, rehab, rent, refinance, and repeat.',
    detail: 'DealSkout models the refinance. The result is not a lender approval.',
  },
  refinanceLtv: {
    id: 'refinanceLtv',
    label: 'Refinance LTV',
    short: 'Requested refinance loan compared with stabilized value.',
    detail: 'The modeled loan cannot exceed stabilized value times the refinance LTV you enter.',
  },
  cashLeftInDeal: {
    id: 'cashLeftInDeal',
    label: 'Cash left in the deal',
    short: 'Cash invested before refinance minus cash returned.',
    detail: 'Zero or negative cash remaining means the model returned all cash. Cash-on-cash is then undefined, not infinite.',
  },
};

export const term = (id: BeginnerTermId): BeginnerTerm => BEGINNER_TERMS[id];
